import { useState, useEffect } from "react";
import { db } from "./firebase";
import { doc, setDoc, getDoc, collection, getDocs, query, orderBy, updateDoc } from "firebase/firestore";
import emailjs from "@emailjs/browser";

const EMAILJS_SERVICE_ID = "service_6s4y6ci";
const EMAILJS_PUBLIC_KEY = "IqticZ4G35CQ30ugZ";
const ADMIN_EMAIL = "lawhaling91069@gmail.com";
const ADMIN_UID = ""; // Set your admin UID here

function generateMusicID(type = "MUL") {
  const year = new Date().getFullYear();
  const num = Math.floor(100000 + Math.random() * 900000);
  return `${type}-${year}-${num}`;
}

export default function UMSApplication({ user, onBack, isAdmin }) {
  const [view, setView] = useState("loading"); // loading, apply, pending, approved, admin
  const [application, setApplication] = useState(null);
  const [allApplications, setAllApplications] = useState([]);

  // Form state
  const [memberType, setMemberType] = useState("listener");
  const [whyJoin, setWhyJoin] = useState("");
  const [favoriteGenre, setFavoriteGenre] = useState("");
  const [favoriteArtist, setFavoriteArtist] = useState("");
  const [country, setCountry] = useState("");
  const [agreeRules, setAgreeRules] = useState(false);
  const [artistRealName, setArtistRealName] = useState("");
  const [artistGenres, setArtistGenres] = useState("");
  const [artistMusicLink, setArtistMusicLink] = useState("");
  const [artistSocialLink, setArtistSocialLink] = useState("");
  const [artistExperience, setArtistExperience] = useState("");
  const [artistReleased, setArtistReleased] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const genres = ["Pop", "Hip-Hop", "R&B", "Rock", "Jazz", "Electronic", "K-Pop", "Myanmar", "Christian", "Country", "Indie", "Classical", "Other"];
  const countries = ["Myanmar", "India", "South Korea", "Japan", "United States", "United Kingdom", "Other"];

  useEffect(() => {
    checkApplicationStatus();
  }, []);

  async function checkApplicationStatus() {
    try {
      // Check if already has approved ID
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists() && userDoc.data().musicId && userDoc.data().memberStatus === "approved") {
        setApplication(userDoc.data());
        setView("approved");
        return;
      }
      // Check if has pending application
      const appDoc = await getDoc(doc(db, "umsApplications", user.uid));
      if (appDoc.exists()) {
        setApplication(appDoc.data());
        setView(appDoc.data().status === "approved" ? "approved" : "pending");
        return;
      }
      setView("apply");
      // Load admin panel if admin
      if (isAdmin) {
        loadAllApplications();
        setView("admin");
      }
    } catch {
      setView("apply");
    }
  }

  async function loadAllApplications() {
    try {
      const q = query(collection(db, "umsApplications"), orderBy("appliedAt", "desc"));
      const snap = await getDocs(q);
      setAllApplications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {}
  }

  async function submitApplication() {
    if (!whyJoin.trim()) { setError("Please tell us why you want to join!"); return; }
    if (!favoriteGenre) { setError("Please select your favorite genre!"); return; }
    if (!country) { setError("Please select your country!"); return; }
    if (!agreeRules) { setError("Please agree to the community guidelines!"); return; }
    if (memberType === "artist") {
      if (!artistRealName.trim()) { setError("Please enter your real full name."); return; }
      if (!artistGenres.trim()) { setError("Please enter your music genres."); return; }
      if (!artistMusicLink.trim()) { setError("Please provide a link to your music."); return; }
      if (!artistSocialLink.trim()) { setError("Please provide your social media link."); return; }
      if (!artistExperience) { setError("Please select your years of experience."); return; }
      if (!artistReleased) { setError("Please indicate if you have released original music."); return; }
    }

    setSubmitting(true); setError("");
    try {
      const app = {
        userId: user.uid,
        userName: user.displayName || user.email?.split("@")[0] || "User",
        userEmail: user.email,
        userPhoto: user.photoURL || "",
        memberType,
        whyJoin: whyJoin.trim(),
        favoriteGenre,
        favoriteArtist: favoriteArtist.trim(),
        country,
        ...(memberType === "artist" && {
          artistRealName: artistRealName.trim(),
          artistGenres: artistGenres.trim(),
          artistMusicLink: artistMusicLink.trim(),
          artistSocialLink: artistSocialLink.trim(),
          artistExperience,
          artistReleased,
        }),
        status: "pending",
        appliedAt: new Date().toISOString(),
        reviewedAt: null,
        musicId: null,
      };

      await setDoc(doc(db, "umsApplications", user.uid), app);

      // Notify admin
      await emailjs.send(EMAILJS_SERVICE_ID, "template_a2ue8ic", {
  user_name: app.userName,
  user_email: app.userEmail,
  signup_time: new Date().toLocaleString(),
  subject: `🌍 New UMS Application - ${app.userName}`,
  message: `🌍 NEW UMS MEMBERSHIP APPLICATION\n\nMember Type: ${memberType === "artist" ? "🎤 Artist Member" : "🎵 Music Lover"}\nCountry: ${country}\nFavorite Genre: ${favoriteGenre}\nFavorite Artist: ${favoriteArtist || "Not specified"}\n\nWhy they want to join:\n"${whyJoin}"\n\nReview at: https://mymusicstream.online`,
}, EMAILJS_PUBLIC_KEY);

      setApplication(app);
      setSuccess("Application submitted successfully!");
      setTimeout(() => setView("pending"), 1500);
    } catch {
      setError("Failed to submit. Please try again.");
    }
    setSubmitting(false);
  }

  async function approveApplication(app) {
    try {
      const musicId = generateMusicID(app.memberType === "artist" ? "MUA" : "MUL");
      
      // Update application
      await updateDoc(doc(db, "umsApplications", app.userId), {
        status: "approved",
        musicId,
        reviewedAt: new Date().toISOString(),
      });

// Update user profile (use setDoc with merge to create if not exists)
      await setDoc(doc(db, "users", app.userId), {
        musicId,
        memberType: app.memberType === "artist" ? "Artist Member" : "Music Lover",
        memberStatus: "approved",
       approvedAt: new Date().toISOString(),
      }, { merge: true });

      // Send approval email
      await emailjs.send(EMAILJS_SERVICE_ID, "template_w544ezp", {
        to_name: app.userName,
        to_email: app.userEmail,
        from_name: "MyMusic Universal Music Society",
        message: `🎉 Congratulations! Your membership application has been APPROVED!\n\nYour Official Music ID: ${musicId}\n\nWelcome to the MyMusic Universal Music Society! You are now an official member of the world's first global music community.\n\nYour profile: https://mymusicstream.online/@${app.userName}\n\nKeep making music and inspiring the world! 🎵`,
        app_link: "https://mymusicstream.online",
      }, EMAILJS_PUBLIC_KEY);

      setAllApplications(prev => prev.map(a => a.id === app.id ? { ...a, status: "approved", musicId } : a));
      alert(`✅ Approved! Music ID: ${musicId} — Email sent to ${app.userEmail}`);
    } catch (err) {
      alert("Error approving application: " + err.message);
    }
  }

  async function rejectApplication(app) {
    const reason = window.prompt("Enter rejection reason (will be sent to user):");
    if (!reason) return;
    try {
      await updateDoc(doc(db, "umsApplications", app.userId), {
        status: "rejected",
        rejectionReason: reason,
        reviewedAt: new Date().toISOString(),
      });

      // Send rejection email
      await emailjs.send(EMAILJS_SERVICE_ID, "template_w544ezp", {
        to_name: app.userName,
        to_email: app.userEmail,
        from_name: "MyMusic Universal Music Society",
        message: `Thank you for applying to MyMusic Universal Music Society.\n\nUnfortunately, your application was not approved at this time.\n\nReason: ${reason}\n\nYou are welcome to apply again after 30 days. Keep enjoying music! 🎵`,
        app_link: "https://mymusicstream.online",
      }, EMAILJS_PUBLIC_KEY);

      setAllApplications(prev => prev.map(a => a.id === app.id ? { ...a, status: "rejected" } : a));
      alert("❌ Rejected. Email sent to user.");
    } catch { alert("Error rejecting application"); }
  }

  function timeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    return `${days} days ago`;
  }

  // ── LOADING ───────────────────────────────────────────────────────────────
  if (view === "loading") return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "#000", overflowY: "auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 32, height: 32, border: "3px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // ── APPROVED ──────────────────────────────────────────────────────────────
  if (view === "approved" && application) return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "#000", overflowY: "auto", color: "#fff", fontFamily: "'DM Sans',sans-serif", padding: 24 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap'); @keyframes glow{0%,100%{box-shadow:0 0 20px rgba(29,185,84,0.3)}50%{box-shadow:0 0 50px rgba(29,185,84,0.6)}}`}</style>
      <button onClick={onBack} style={{ background: "#111", border: "1px solid #222", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", marginBottom: 24 }}>
        <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
      </button>

      {/* ID Card */}
      <div style={{ background: "linear-gradient(135deg, #0d2818, #111)", borderRadius: 20, padding: 24, border: "1px solid #1db954", marginBottom: 24, animation: "glow 3s infinite", maxWidth: 340, margin: "0 auto 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 10, color: "#1db954", fontWeight: 700, letterSpacing: "0.15em", marginBottom: 4 }}>🌍 UNIVERSAL MUSIC SOCIETY</div>
            <div style={{ fontSize: 10, color: "#555", letterSpacing: "0.1em" }}>OFFICIAL MEMBERSHIP CARD</div>
          </div>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#1db954", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🎵</div>
        </div>

        <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#111", overflow: "hidden", border: "2px solid #1db954", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {user.photoURL ? <img src={user.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 24 }}>👤</span>}
        </div>

        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 800, marginBottom: 4 }}>{user.displayName || application.userName}</div>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 16, fontFamily: "monospace" }}>{application.musicId}</div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          <div style={{ padding: "4px 10px", borderRadius: 50, background: "rgba(29,185,84,0.15)", border: "1px solid rgba(29,185,84,0.3)", fontSize: 11, color: "#1db954", fontWeight: 600 }}>
            ✓ {application.memberType === "artist" ? "Artist Member" : "Music Lover"}
          </div>
          <div style={{ padding: "4px 10px", borderRadius: 50, background: "rgba(29,185,84,0.1)", border: "1px solid #1a1a1a", fontSize: 11, color: "#555" }}>
            {application.favoriteGenre}
          </div>
        </div>

        <div style={{ borderTop: "1px solid #1a1a1a", paddingTop: 12, display: "flex", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 9, color: "#444", letterSpacing: "0.1em" }}>MEMBER SINCE</div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{new Date(application.appliedAt).getFullYear()}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 9, color: "#444", letterSpacing: "0.1em" }}>PROFILE</div>
            <div style={{ fontSize: 11, color: "#1db954", marginTop: 2 }}>@{user.displayName || application.userName}</div>
          </div>
        </div>
      </div>

      <div style={{ textAlign: "center" }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Welcome, Official Member! 🎉</div>
        <div style={{ fontSize: 13, color: "#555", marginBottom: 20 }}>You are part of the world's first Universal Music Society</div>
        <button onClick={() => { navigator.clipboard.writeText(application.musicId); alert("Music ID copied!"); }}
          style={{ padding: "12px 24px", borderRadius: 50, border: "1px solid #2a2a2a", background: "transparent", color: "#fff", fontSize: 13, cursor: "pointer", marginRight: 10 }}>
          📋 Copy ID
        </button>
        <button onClick={() => { const text = `🎵 I'm an official member of MyMusic Universal Music Society!\nMy Music ID: ${application.musicId}\nJoin at: mymusicstream.online`; if (navigator.share) navigator.share({ title: "My Music ID", text }); else { navigator.clipboard.writeText(text); alert("Copied!"); } }}
          style={{ padding: "12px 24px", borderRadius: 50, border: "none", background: "#1db954", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          📱 Share My ID
        </button>
      </div>
    </div>
  );

  // ── PENDING ───────────────────────────────────────────────────────────────
  if (view === "pending") return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "#000", overflowY: "auto", color: "#fff", fontFamily: "'DM Sans',sans-serif", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>
      <button onClick={onBack} style={{ position: "absolute", top: 20, left: 16, background: "#111", border: "1px solid #222", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
      </button>
      <div style={{ fontSize: 64, marginBottom: 20 }}>⏳</div>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 24, fontWeight: 800, marginBottom: 12 }}>Application Under Review</div>
      <div style={{ fontSize: 14, color: "#666", lineHeight: 1.8, maxWidth: 300, marginBottom: 24 }}>
        Your application to join the MyMusic Universal Music Society is being reviewed. This usually takes 1-2 weeks.
      </div>
      <div style={{ background: "#111", borderRadius: 16, padding: 20, border: "1px solid #1a1a1a", maxWidth: 300, width: "100%", marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: "#555", marginBottom: 8 }}>Applied</div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{application ? timeAgo(application.appliedAt) : ""}</div>
        <div style={{ fontSize: 12, color: "#555", marginTop: 12, marginBottom: 8 }}>Member Type</div>
        <div style={{ fontSize: 14, fontWeight: 600, textTransform: "capitalize" }}>{application?.memberType === "artist" ? "🎤 Artist Member" : "🎵 Music Lover"}</div>
        <div style={{ fontSize: 12, color: "#555", marginTop: 12, marginBottom: 8 }}>Status</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#f59e0b" }}>⏳ Pending Review</div>
      </div>
      <div style={{ fontSize: 12, color: "#333" }}>You'll receive an email when your application is reviewed!</div>
    </div>
  );

  // ── ADMIN PANEL ───────────────────────────────────────────────────────────
  if (view === "admin") return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "#000", overflowY: "auto", color: "#fff", fontFamily: "'DM Sans',sans-serif", paddingBottom: 100 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>
      <div style={{ padding: "20px 16px", borderBottom: "1px solid #111" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <button onClick={onBack} style={{ background: "#111", border: "1px solid #222", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 800 }}>⚙️ UMS Admin Panel</div>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          {[["pending", "#f59e0b"], ["approved", "#1db954"], ["rejected", "#e91429"]].map(([status, color]) => (
            <div key={status} style={{ background: "#111", borderRadius: 10, padding: "8px 14px", border: `1px solid ${color}22` }}>
              <div style={{ fontSize: 18, fontWeight: 800, color, fontFamily: "'Syne',sans-serif" }}>{allApplications.filter(a => a.status === status).length}</div>
              <div style={{ fontSize: 11, color: "#555", textTransform: "capitalize" }}>{status}</div>
            </div>
          ))}
        </div>
      </div>

      {allApplications.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "#555" }}>No applications yet</div>
      ) : allApplications.map(app => (
        <div key={app.id} style={{ padding: 16, borderBottom: "1px solid #0a0a0a" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#111", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {app.userPhoto ? <img src={app.userPhoto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span>👤</span>}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{app.userName}</div>
              <div style={{ fontSize: 12, color: "#555" }}>{app.userEmail}</div>
              <div style={{ fontSize: 11, color: "#444", marginTop: 2 }}>{timeAgo(app.appliedAt)} · {app.country} · {app.favoriteGenre}</div>
            </div>
            <div style={{ padding: "4px 10px", borderRadius: 50, background: app.status === "approved" ? "rgba(29,185,84,0.15)" : app.status === "rejected" ? "rgba(233,20,41,0.15)" : "rgba(245,158,11,0.15)", color: app.status === "approved" ? "#1db954" : app.status === "rejected" ? "#e91429" : "#f59e0b", fontSize: 11, fontWeight: 700 }}>
              {app.status === "approved" ? "✓ Approved" : app.status === "rejected" ? "✕ Rejected" : "⏳ Pending"}
            </div>
          </div>
          <div style={{ background: "#0a0a0a", borderRadius: 10, padding: 12, marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>Why they want to join:</div>
            <div style={{ fontSize: 13, color: "#ccc", lineHeight: 1.6 }}>{app.whyJoin}</div>
            {app.favoriteArtist && <div style={{ fontSize: 12, color: "#555", marginTop: 6 }}>Favorite artist: {app.favoriteArtist}</div>}
           {app.memberType === "artist" && (
              <div style={{ background: "rgba(29,185,84,0.05)", borderRadius: 10, padding: 12, marginBottom: 12, border: "1px solid rgba(29,185,84,0.2)" }}>
                <div style={{ fontSize: 12, color: "#1db954", fontWeight: 700, marginBottom: 8 }}>🎤 Artist Verification Info</div>
                {app.artistRealName && <div style={{ fontSize: 12, color: "#ccc", marginBottom: 4 }}>Real Name: <span style={{ color: "#fff" }}>{app.artistRealName}</span></div>}
                {app.artistGenres && <div style={{ fontSize: 12, color: "#ccc", marginBottom: 4 }}>Genres: <span style={{ color: "#fff" }}>{app.artistGenres}</span></div>}
                {app.artistExperience && <div style={{ fontSize: 12, color: "#ccc", marginBottom: 4 }}>Experience: <span style={{ color: "#fff" }}>{app.artistExperience}</span></div>}
                {app.artistReleased && <div style={{ fontSize: 12, color: "#ccc", marginBottom: 4 }}>Released Music: <span style={{ color: "#fff" }}>{app.artistReleased}</span></div>}
                {app.artistMusicLink && <div style={{ fontSize: 12, color: "#ccc", marginBottom: 4 }}>Music Link: <a href={app.artistMusicLink} target="_blank" rel="noreferrer" style={{ color: "#1db954" }}>{app.artistMusicLink}</a></div>}
                {app.artistSocialLink && <div style={{ fontSize: 12, color: "#ccc" }}>Social: <a href={app.artistSocialLink} target="_blank" rel="noreferrer" style={{ color: "#1db954" }}>{app.artistSocialLink}</a></div>}
              </div>
            )}
            <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>Member type: {app.memberType === "artist" ? "🎤 Artist" : "🎵 Music Lover"}</div>
            {app.musicId && <div style={{ fontSize: 12, color: "#1db954", marginTop: 4 }}>Music ID: {app.musicId}</div>}
          </div>
          {app.status === "pending" && (
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => approveApplication(app)} style={{ flex: 1, padding: "10px", borderRadius: 50, border: "none", background: "#1db954", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>✓ Approve & Issue ID</button>
              <button onClick={() => rejectApplication(app)} style={{ flex: 1, padding: "10px", borderRadius: 50, border: "1px solid #e91429", background: "transparent", color: "#e91429", fontSize: 13, cursor: "pointer" }}>✕ Reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // ── APPLICATION FORM ──────────────────────────────────────────────────────
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "#000", overflowY: "auto", color: "#fff", fontFamily: "'DM Sans',sans-serif", paddingBottom: 100 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #0d2818, #000)", padding: "20px 16px 24px" }}>
        <button onClick={onBack} style={{ background: "rgba(0,0,0,0.3)", border: "none", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", marginBottom: 16 }}>
          <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div style={{ fontSize: 10, color: "#1db954", fontWeight: 700, letterSpacing: "0.15em", marginBottom: 8 }}>🌍 UNIVERSAL MUSIC SOCIETY</div>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 24, fontWeight: 800, marginBottom: 8 }}>Apply for Membership</div>
        <div style={{ fontSize: 13, color: "#666", lineHeight: 1.7 }}>Join the world's first Universal Music Society and get your official Music ID!</div>
      </div>

      <div style={{ padding: 16 }}>
        {error && <div style={{ background: "rgba(233,20,41,0.1)", border: "1px solid rgba(233,20,41,0.3)", color: "#ff4d4d", padding: 12, borderRadius: 10, fontSize: 13, marginBottom: 16, textAlign: "center" }}>{error}</div>}
        {success && <div style={{ background: "rgba(29,185,84,0.1)", border: "1px solid rgba(29,185,84,0.3)", color: "#1db954", padding: 12, borderRadius: 10, fontSize: 13, marginBottom: 16, textAlign: "center" }}>{success}</div>}

        {/* Member Type */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>I am a... *</div>
          <div style={{ display: "flex", gap: 10 }}>
            {[["listener", "🎵 Music Lover", "I love listening to music"], ["artist", "🎤 Artist", "I create original music"]].map(([type, label, desc]) => (
              <div key={type} onClick={() => setMemberType(type)}
                style={{ flex: 1, padding: 14, borderRadius: 14, border: `1.5px solid ${memberType === type ? "#1db954" : "#2a2a2a"}`, background: memberType === type ? "rgba(29,185,84,0.08)" : "#0a0a0a", cursor: "pointer" }}>
                <div style={{ fontSize: 18, marginBottom: 4 }}>{label.split(" ")[0]}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: memberType === type ? "#1db954" : "#fff", marginBottom: 4 }}>{label.split(" ").slice(1).join(" ")}</div>
                <div style={{ fontSize: 11, color: "#555" }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Why Join */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Why do you want to join? *</div>
          <textarea value={whyJoin} onChange={e => setWhyJoin(e.target.value)} placeholder="Tell us your passion for music and why you want to be part of this society..."
            style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none", resize: "vertical", minHeight: 100 }} />
          <div style={{ fontSize: 11, color: "#333", marginTop: 4 }}>{whyJoin.length}/500 characters</div>
        </div>

        {/* Favorite Genre */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Favorite Genre *</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {genres.map(g => (
              <button key={g} onClick={() => setFavoriteGenre(g)} style={{ padding: "7px 14px", borderRadius: 50, border: `1px solid ${favoriteGenre === g ? "#1db954" : "#2a2a2a"}`, background: favoriteGenre === g ? "rgba(29,185,84,0.1)" : "transparent", color: favoriteGenre === g ? "#1db954" : "#666", fontSize: 12, cursor: "pointer" }}>
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* Favorite Artist */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Favorite Artist (Optional)</div>
          <input value={favoriteArtist} onChange={e => setFavoriteArtist(e.target.value)} placeholder="Who inspires you most?"
            style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none" }} />
        </div>

        {/* Country */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Country *</div>
          <select value={country} onChange={e => setCountry(e.target.value)}
            style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none" }}>
            <option value="">Select your country</option>
            {countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Community Guidelines */}
        <div style={{ background: "#0a0a0a", borderRadius: 14, padding: 16, marginBottom: 20, border: "1px solid #1a1a1a" }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 12 }}>📜 Community Guidelines</div>
          {["Respect all musicians and music lovers worldwide", "Share only original content you own", "No hate speech, discrimination or harassment", "Support and encourage fellow artists", "Represent MyMusic Society with pride and integrity"].map((rule, i) => (
            <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8 }}>
              <span style={{ color: "#1db954", flexShrink: 0 }}>✓</span>
              <span style={{ fontSize: 13, color: "#888" }}>{rule}</span>
            </div>
          ))}
        </div>

       {/* Artist-specific verification fields */}
        {memberType === "artist" && (
          <div style={{ background: "rgba(29,185,84,0.05)", borderRadius: 14, padding: 16, border: "1px solid rgba(29,185,84,0.2)", marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1db954", marginBottom: 12 }}>🎤 Artist Verification Requirements</div>
            
            <div style={{ fontSize: 12, color: "#888", marginBottom: 16, lineHeight: 1.6 }}>
              To become a Verified Artist on MyMusic, you must provide proof that you are a real musician. All fields below are required.
            </div>

            <div style={{ fontSize: 11, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Real Full Name *</div>
            <input value={artistRealName} onChange={e => setArtistRealName(e.target.value)} placeholder="Your legal full name"
              style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontSize: 14, outline: "none", marginBottom: 14, boxSizing: "border-box" }} />

            <div style={{ fontSize: 11, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Music Genre(s) You Create *</div>
            <input value={artistGenres} onChange={e => setArtistGenres(e.target.value)} placeholder="e.g. Myanmar Pop, R&B, Hip Hop"
              style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontSize: 14, outline: "none", marginBottom: 14, boxSizing: "border-box" }} />

            <div style={{ fontSize: 11, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Link to Your Music (YouTube/SoundCloud/Facebook) *</div>
            <input value={artistMusicLink} onChange={e => setArtistMusicLink(e.target.value)} placeholder="https://youtube.com/..."
              style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontSize: 14, outline: "none", marginBottom: 14, boxSizing: "border-box" }} />

            <div style={{ fontSize: 11, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Social Media Profile Link *</div>
            <input value={artistSocialLink} onChange={e => setArtistSocialLink(e.target.value)} placeholder="https://facebook.com/... or Instagram"
              style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontSize: 14, outline: "none", marginBottom: 14, boxSizing: "border-box" }} />

            <div style={{ fontSize: 11, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Years of Experience *</div>
            <select value={artistExperience} onChange={e => setArtistExperience(e.target.value)}
              style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontSize: 14, outline: "none", marginBottom: 14 }}>
              <option value="">Select experience</option>
              <option value="less1">Less than 1 year</option>
              <option value="1-3">1-3 years</option>
              <option value="3-5">3-5 years</option>
              <option value="5+">5+ years</option>
            </select>

            <div style={{ fontSize: 11, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Have you released original music? *</div>
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              {["Yes, officially released", "Yes, independently", "Not yet but actively creating"].map(opt => (
                <div key={opt} onClick={() => setArtistReleased(opt)} style={{ flex: 1, padding: "10px 8px", borderRadius: 10, border: `1px solid ${artistReleased === opt ? "#1db954" : "#2a2a2a"}`, background: artistReleased === opt ? "rgba(29,185,84,0.1)" : "#0a0a0a", cursor: "pointer", fontSize: 11, color: artistReleased === opt ? "#1db954" : "#666", textAlign: "center" }}>
                  {opt}
                </div>
              ))}
            </div>

            <div style={{ fontSize: 11, color: "#f59e0b", lineHeight: 1.6, padding: "10px 12px", background: "rgba(245,158,11,0.1)", borderRadius: 8 }}>
              ⚠️ Providing false information will result in permanent ban. All applications are manually reviewed by our team within 3-5 business days.
            </div>
          </div>
        )}
        {/* Agree checkbox */}
        <div onClick={() => setAgreeRules(!agreeRules)} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, cursor: "pointer" }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${agreeRules ? "#1db954" : "#2a2a2a"}`, background: agreeRules ? "#1db954" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {agreeRules && <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#000", strokeWidth: 3 }}><polyline points="20 6 9 17 4 12"/></svg>}
          </div>
          <span style={{ fontSize: 13, color: "#888" }}>I agree to the Community Guidelines and Terms of Service</span>
        </div>

        <button onClick={submitApplication} disabled={submitting}
          style={{ width: "100%", padding: 16, borderRadius: 50, border: "none", background: submitting ? "#333" : "#1db954", color: submitting ? "#666" : "#000", fontSize: 15, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer", marginBottom: 12 }}>
          {submitting ? "Submitting..." : "🌍 Submit Application"}
        </button>
        <p style={{ textAlign: "center", fontSize: 12, color: "#333" }}>Review takes 1-2 weeks · You'll be notified by email</p>
      </div>
    </div>
  );
}
